import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Vote, ShieldAlert, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";

export default function ListeCetfSpace() {
  const [state, setState] = useState({ status: "loading" });
  const [soutienChecked, setSoutienChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/liste-cetf/space")
      .then((r) => setState({ status: "ok", data: r.data }))
      .catch(() => setState({ status: "forbidden" }));
  };
  useEffect(() => { load(); }, []);

  const acceptSoutien = async () => {
    setSaving(true);
    try {
      await api.post("/liste-cetf/soutien");
      toast.success("Consentement enregistré — bienvenue dans l'espace Liste CET !");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-emerald-50/80 to-white min-h-[60vh]" data-testid="liste-cetf-space">
      <div className="liste-watermark" style={{ backgroundImage: "url(/assets/logo-tree.png)" }} aria-hidden="true" />
      <div className="relative z-[1] max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0 shadow-md">
            <Vote className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Espace restreint</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="liste-cetf-space-title">Liste CETF — Espace candidats</h1>
          </div>
        </div>

        {state.status === "loading" && <p className="text-sm text-slate-400">Chargement…</p>}

        {state.status === "forbidden" && (
          <Card className="border-amber-200 bg-amber-50" data-testid="liste-cetf-forbidden">
            <CardContent className="p-6 flex items-start gap-4">
              <ShieldAlert className="h-8 w-8 text-amber-600 shrink-0" />
              <div className="space-y-2">
                <p className="font-semibold text-amber-900">Accès réservé aux candidats validés</p>
                <p className="text-sm text-amber-800 leading-relaxed">
                  Votre candidature n'est pas encore validée (ou aucune candidature n'est enregistrée avec l'email de votre compte).
                  Déposez votre candidature puis attendez la validation du bureau.
                </p>
                <Button variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100" asChild>
                  <Link to="/liste-cetf">Déposer ma candidature</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {state.status === "ok" && !state.data.me.consent_soutien && (
          <Card className="border-emerald-300 shadow-md" data-testid="soutien-gate">
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-bold text-slate-900">Avant d'accéder à l'espace</h2>
              <div className="flex items-start gap-3 bg-emerald-50 rounded-xl p-4">
                <Checkbox id="soutien-check" className="mt-0.5" data-testid="soutien-checkbox"
                  checked={soutienChecked} onCheckedChange={(v) => setSoutienChecked(v === true)} />
                <label htmlFor="soutien-check" className="text-sm leading-relaxed text-slate-700 cursor-pointer">
                  <strong>« Soutenu par Cœur d'École Toury Famille association »</strong> — je consens à ce que ma candidature
                  s'inscrive dans le cadre de l'association de fait entre parents d'élèves de l'école élémentaire Julien Mercier
                  de la commune de Toury, soutenue par l'association CETF.
                </label>
              </div>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={acceptSoutien}
                disabled={!soutienChecked || saving} data-testid="soutien-submit-btn">
                {saving ? "Enregistrement…" : "J'accepte et j'accède à l'espace"}
              </Button>
            </CardContent>
          </Card>
        )}

        {state.status === "ok" && state.data.me.consent_soutien && (
          <>
            <Card className="border-emerald-200 bg-emerald-600 text-white shadow-lg" data-testid="referent-banner">
              <CardContent className="p-5 flex items-center gap-4">
                <Star className="h-8 w-8 text-amber-300 shrink-0" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-emerald-100">Référente de liste par intérim</p>
                  <p className="text-xl font-extrabold" data-testid="referent-name">
                    {state.data.interim_referent || "À nommer par le bureau"}
                  </p>
                  {state.data.list_lead && (
                    <p className="text-sm text-emerald-100 mt-0.5">Chef·fe de liste : {state.data.list_lead}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 overflow-hidden" data-testid="candidates-table-card">
              <div className="overflow-x-auto">
                <Table data-testid="candidates-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Prénom</TableHead>
                      <TableHead>Classe de l'enfant</TableHead>
                      {state.data.privileged && <TableHead>Téléphone</TableHead>}
                      {state.data.privileged && <TableHead>Email</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {state.data.candidates.map((c) => (
                      <TableRow key={c.id} data-testid={`candidate-row-${c.id}`}>
                        <TableCell className="font-medium">
                          {c.last_name}
                          {c.is_interim_referent && <span className="block text-xs text-emerald-700 font-semibold">Référente par intérim</span>}
                          {c.is_list_lead && <span className="block text-xs text-amber-700 font-semibold">Chef·fe de liste</span>}
                        </TableCell>
                        <TableCell>{c.first_name}</TableCell>
                        <TableCell className="text-sm">{c.child_class}</TableCell>
                        {state.data.privileged && <TableCell className="text-sm">{c.phone || "—"}</TableCell>}
                        {state.data.privileged && <TableCell className="text-sm">{c.email || "—"}</TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
            <p className="text-xs text-slate-400 leading-relaxed">
              Les coordonnées complètes (téléphone, email) ne sont visibles que par la référente de liste par intérim,
              le ou la chef·fe de liste et le super-admin.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
